import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Play, Code, Eye, Clipboard, Check, Trash2, Monitor, Smartphone, Tablet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type DevicePreview = 'desktop' | 'tablet' | 'mobile';

export default function EmbedTester() {
  const navigate = useNavigate();
  const [embedCode, setEmbedCode] = useState('');
  const [isRendered, setIsRendered] = useState(false);
  const [pasted, setPasted] = useState(false);
  const [devicePreview, setDevicePreview] = useState<DevicePreview>('desktop');
  const previewRef = useRef<HTMLDivElement>(null);

  // Pulisce l'iframe quando il codice cambia
  useEffect(() => {
    if (!embedCode) {
      setIsRendered(false);
    }
  }, [embedCode]);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setEmbedCode(text);
      setPasted(true);
      setTimeout(() => setPasted(false), 2000);
    } catch (err) {
      console.error('Errore lettura clipboard:', err);
      alert('Non riesco a leggere dalla clipboard. Incolla manualmente il codice.');
    }
  };

  const handleRender = () => {
    if (!embedCode.trim()) {
      alert('Inserisci prima il codice embed!');
      return;
    }
    setIsRendered(true);
  };

  const handleClear = () => {
    setEmbedCode('');
    setIsRendered(false);
  };

  // Estrai l'URL dell'iframe dal codice embed
  const extractIframeSrc = (code: string): string | null => {
    // Match iframe src
    const iframeMatch = code.match(/src=["']([^"']+)["']/);
    if (iframeMatch) {
      return iframeMatch[1];
    }
    // Match script-based embed
    const scriptMatch = code.match(/iframe\.src\s*=\s*['"]([^'"]+)['"]/);
    if (scriptMatch) {
      return scriptMatch[1];
    }
    return null;
  };

  const iframeSrc = extractIframeSrc(embedCode);

  const getDeviceWidth = () => {
    switch (devicePreview) {
      case 'mobile': return 'max-w-[375px]';
      case 'tablet': return 'max-w-[768px]';
      default: return 'max-w-full';
    }
  };

  const getDeviceHeight = () => {
    switch (devicePreview) {
      case 'mobile': return 'h-[667px]';
      case 'tablet': return 'h-[700px]';
      default: return 'h-full';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Torna allo Studio</span>
            </button>
            <div className="h-6 w-px bg-white/20" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-lg flex items-center justify-center">
                <Code className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Chat Embedded Tester</h1>
                <p className="text-xs text-white/50">Testa il widget chat come apparirà sui siti web</p>
              </div>
            </div>
          </div>

          {/* Device Preview Selector */}
          <div className="flex items-center gap-2 bg-white/10 rounded-lg p-1">
            <button
              onClick={() => setDevicePreview('desktop')}
              className={`p-2 rounded-md transition-all ${devicePreview === 'desktop' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
              title="Desktop"
            >
              <Monitor className="w-5 h-5" />
            </button>
            <button
              onClick={() => setDevicePreview('tablet')}
              className={`p-2 rounded-md transition-all ${devicePreview === 'tablet' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
              title="Tablet"
            >
              <Tablet className="w-5 h-5" />
            </button>
            <button
              onClick={() => setDevicePreview('mobile')}
              className={`p-2 rounded-md transition-all ${devicePreview === 'mobile' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
              title="Mobile"
            >
              <Smartphone className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-140px)]">
          {/* Left Panel - Code Input */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden flex flex-col">
            <div className="bg-white/5 px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-semibold text-white">Codice Embed</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePaste}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg transition-all text-sm font-medium"
                >
                  {pasted ? <Check className="w-4 h-4" /> : <Clipboard className="w-4 h-4" />}
                  {pasted ? 'Incollato!' : 'Incolla'}
                </button>
                <button
                  onClick={handleClear}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-all text-sm font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Pulisci
                </button>
              </div>
            </div>
            
            <div className="flex-1 p-4">
              <textarea
                value={embedCode}
                onChange={(e) => setEmbedCode(e.target.value)}
                placeholder={`Incolla qui il codice embed generato dal bottone "Embed"...

Esempio iframe:
<iframe 
  src="http://localhost:5173/embed?flow=..."
  width="100%"
  height="600px"
  ...
></iframe>

Esempio script:
<script>
  (function() {
    var iframe = document.createElement('iframe');
    iframe.src = '...';
    ...
  })();
</script>`}
                className="w-full h-full bg-black/30 text-green-400 font-mono text-sm p-4 rounded-xl border border-white/10 resize-none focus:outline-none focus:border-cyan-500/50 placeholder-white/20"
              />
            </div>

            <div className="px-4 pb-4">
              <button
                onClick={handleRender}
                disabled={!embedCode.trim()}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${
                  embedCode.trim()
                    ? 'bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                }`}
              >
                <Play className="w-5 h-5" />
                Visualizza Anteprima
              </button>
            </div>

            {/* Instructions */}
            <div className="px-4 pb-4">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                <h4 className="text-amber-400 font-semibold text-sm mb-2">💡 Come usare</h4>
                <ol className="text-amber-200/70 text-xs space-y-1 list-decimal list-inside">
                  <li>Vai allo Flow Studio e configura il tuo flow</li>
                  <li>Clicca sul bottone <strong className="text-amber-300">"Embed"</strong> nella toolbar</li>
                  <li>Copia il codice generato (iframe o script)</li>
                  <li>Incolla il codice qui e clicca <strong className="text-amber-300">"Visualizza Anteprima"</strong></li>
                </ol>
              </div>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden flex flex-col">
            <div className="bg-white/5 px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <Eye className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold text-white">Anteprima Widget</span>
              <span className="text-xs text-white/40 ml-2">
                ({devicePreview === 'desktop' ? 'Desktop' : devicePreview === 'tablet' ? 'Tablet' : 'Mobile'})
              </span>
            </div>
            
            <div 
              ref={previewRef}
              className="flex-1 p-4 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 overflow-auto"
            >
              {isRendered && iframeSrc ? (
                <div className={`${getDeviceWidth()} ${getDeviceHeight()} w-full transition-all duration-300`}>
                  <div className="w-full h-full bg-white rounded-xl shadow-2xl overflow-hidden border-4 border-gray-300">
                    {/* Simulated browser chrome */}
                    <div className="bg-gray-200 px-3 py-2 flex items-center gap-2 border-b border-gray-300">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-400" />
                        <div className="w-3 h-3 rounded-full bg-yellow-400" />
                        <div className="w-3 h-3 rounded-full bg-green-400" />
                      </div>
                      <div className="flex-1 bg-white rounded px-3 py-1 text-xs text-gray-500 truncate">
                        esempio-sito-web.com
                      </div>
                    </div>
                    {/* Iframe content */}
                    <iframe
                      src={iframeSrc}
                      className="w-full h-[calc(100%-40px)] border-none"
                      title="Chat Widget Preview"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-24 h-24 bg-white/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Eye className="w-12 h-12 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">Nessuna anteprima</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Incolla il codice embed e clicca "Visualizza Anteprima"
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
